import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, FileText, Loader2 } from "lucide-react";
import { usePublicSearch } from "@/hooks/useDocSearch";
import AlgoliaHighlight from "@/components/AlgoliaHighlight";
import { layoutT } from "@/lib/publicI18n";

/** Clickable search field — opens the Algolia modal (navbar, hero, etc.). */
export function PublicSearchTrigger({
  placeholder,
  className = "",
  inputClassName = "",
  iconClassName = "w-4 h-4",
  testId = "public-search-trigger",
  onOpen,
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={testId}
      className={`relative w-full text-left ${className}`}
    >
      <Search className={`${iconClassName} absolute ltr:left-3.5 rtl:right-3.5 top-1/2 -translate-y-1/2 text-[var(--pp-text-dim)] pointer-events-none`} />
      <span
        className={`block w-full ltr:pl-10 ltr:pr-3 rtl:pr-10 rtl:pl-3 py-2 text-sm bg-[var(--pp-surface)] border border-[var(--pp-border)] hover:border-[var(--pp-border-hover)] focus:border-[var(--pp-accent)] rounded-full text-[var(--pp-text-dim)] cursor-text ${inputClassName}`}
      >
        {placeholder}
      </span>
    </button>
  );
}

export default function PublicAlgoliaSearch({
  open,
  onClose,
  slug,
  basePath,
  lang,
  setLang,
  primary,
  initialQuery = "",
}) {
  const navigate = useNavigate();
  const lt = layoutT(lang);
  const [query, setQuery] = useState(initialQuery);
  const { results, loading } = usePublicSearch({
    slug,
    lang: "all",
    query,
    limit: 12,
    enabled: open,
  });

  useEffect(() => {
    if (open) setQuery(initialQuery || "");
  }, [open, initialQuery]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const go = (hit) => {
    const hitLang = hit.lang === "ar" ? "ar" : "en";
    if (hitLang !== lang) {
      setLang(hitLang);
      localStorage.setItem("docs_lang", hitLang);
    }
    onClose();
    setQuery("");
    navigate(`${basePath}/a/${hit.slug}`);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] px-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="public-algolia-search-modal"
    >
      <div
        className="w-full max-w-2xl bg-[var(--pp-surface)] border border-[var(--pp-border)] rounded-2xl overflow-hidden shadow-2xl"
        style={{ boxShadow: `0 24px 80px rgba(0,0,0,0.5), 0 0 0 1px ${primary}33` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 border-b border-[var(--pp-border)]">
          <Search className="w-5 h-5 text-[var(--pp-text-dim)] shrink-0" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={lt.searchArticles}
            data-testid="public-algolia-search-input"
            className="flex-1 py-4 text-base bg-transparent text-[var(--pp-text)] placeholder-[var(--pp-text-dim)] outline-none"
          />
          {loading && <Loader2 className="w-5 h-5 animate-spin text-[var(--pp-text-dim)] shrink-0" />}
        </div>

        <div className="max-h-[55vh] overflow-y-auto">
          {query.trim().length < 2 ? (
            <div className="px-5 py-10 text-center text-sm text-[var(--pp-text-muted)]">
              {lt.searchHint}
            </div>
          ) : results.length === 0 && !loading ? (
            <div className="px-5 py-10 text-center text-sm text-[var(--pp-text-muted)]">
              {lt.noResults} &quot;{query}&quot;
            </div>
          ) : (
            results.map((r) => (
              <button
                key={r.objectID || r.id}
                type="button"
                onClick={() => go(r)}
                data-testid={`public-search-hit-${r.slug}`}
                className="w-full flex items-start gap-3 px-5 py-4 text-left hover:bg-[var(--pp-surface-hover)] border-b border-[var(--pp-border)] last:border-0 transition-colors"
              >
                <FileText className="w-4 h-4 mt-0.5 shrink-0" style={{ color: primary }} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[var(--pp-text)] line-clamp-2">
                    <AlgoliaHighlight html={r._highlight?.title} text={r.title} query={query} />
                  </div>
                  <div className="text-xs text-[var(--pp-text-dim)] mt-1 flex items-center gap-2 flex-wrap">
                    {r.topic_name && <span>{r.topic_name}</span>}
                    <span
                      className="text-[10px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded"
                      style={{
                        background: r.lang === "ar" ? `${primary}33` : "var(--pp-surface-hover)",
                        color: r.lang === "ar" ? primary : "var(--pp-text-muted)",
                      }}
                    >
                      {r.lang === "ar" ? "AR" : "EN"}
                    </span>
                    {r.lang !== lang && (
                      <span className="text-[10px] text-[var(--pp-accent)]">{lt.switchLangHint}</span>
                    )}
                  </div>
                  {(r._highlight?.excerpt || r.excerpt) && (
                    <div className="text-xs text-[var(--pp-text-muted)] line-clamp-2 mt-1.5">
                      <AlgoliaHighlight html={r._highlight?.excerpt} text={r.excerpt} query={query} />
                    </div>
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        {query.trim().length >= 2 && results.length > 0 && (
          <div className="px-5 py-3 border-t border-[var(--pp-border)] text-center">
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate(`${basePath}/search?q=${encodeURIComponent(query)}`);
              }}
              className="text-xs font-black uppercase tracking-wider text-[var(--pp-accent)] hover:opacity-80"
            >
              {lt.viewAllResults}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
